import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { OfficeOpenIcon } from "./OfficeOpenIcons";
import "../redesign/contactRedesign.css";
import "./officeOpen.css";
import { alleFenster, aktivesDokument, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";
import { istDom } from "../windowManager/browserFenster/domArt";

const focusable = (element: HTMLElement) => Array.from(element.querySelectorAll<HTMLElement>(
  'button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]',
));

/** Refined-Schale: am Dokument verankert oder als kompaktes Anlage-Popup. */
export function OfficeOpenSurface({ presentation = "dialog", anchor, title,
  onDismiss, busy = false, children }: {
  presentation?: "dialog" | "popover";
  anchor?: HTMLElement;
  title: string;
  onDismiss(): void;
  busy?: boolean;
  children: React.ReactNode;
}) {
  const surface = React.useRef<HTMLDivElement>(null);
  const callbacks = React.useRef({ onDismiss, busy });
  callbacks.current = { onDismiss, busy };
  const headingId = React.useId();
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);

  const place = React.useCallback(() => {
    if (presentation !== "popover" || !surface.current) return;
    const width = Math.min(320, aktivesFenster().innerWidth - 16);
    const height = surface.current.offsetHeight;
    // Ein Anker im Popup selbst wandert mit jeder Platzierung mit.
    const rect = anchor?.isConnected && !surface.current.contains(anchor) ? anchor.getBoundingClientRect() : null;
    const left = Math.max(8, Math.min(rect?.left ?? (aktivesFenster().innerWidth - width) / 2, aktivesFenster().innerWidth - width - 8));
    const below = rect ? rect.bottom + 6 : 80;
    const above = rect ? rect.top - height - 6 : 80;
    const top = Math.max(8, Math.min(below + height > aktivesFenster().innerHeight - 8 && above >= 8 ? above : below, aktivesFenster().innerHeight - height - 8));
    if (!Number.isFinite(top) || !Number.isFinite(left)) return;
    setPosition(old => old?.top === top && old.left === left ? old : { top, left });
  }, [anchor, presentation]);

  // Nur beim Einhängen und bei neuem Anker messen, nicht nach jedem Render:
  // Sonst löst jede abweichende Messung sofort den nächsten Render aus, und
  // React bricht die ganze Oberfläche mit „Maximum update depth“ ab. Spätere
  // Größenänderungen (Fehlertext, Statuszeile) meldet der ResizeObserver.
  React.useLayoutEffect(place, [place]);
  React.useEffect(() => {
    if (presentation !== "popover") return;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (surface.current) observer?.observe(surface.current);
    alleFenster.addEventListener("resize", place);
    alleFenster.addEventListener("scroll", place, true);
    return () => {
      observer?.disconnect();
      alleFenster.removeEventListener("resize", place);
      alleFenster.removeEventListener("scroll", place, true);
    };
  }, [place, presentation]);

  React.useEffect(() => {
    // Die erste Positionsberechnung rendert das Popover noch unsichtbar.
    // Erst nach diesem Render kann ein Browser den Fokus hineinsetzen.
    if (presentation === "popover" && !position) return;
    const element = surface.current;
    if (!element) return;
    const previous = anchor || aktivesDokument().activeElement;
    element.focus();
    const outside = (event: PointerEvent) => {
      if (presentation !== "popover" || callbacks.current.busy) return;
      if (istDom(event.target, "Node") && !element.contains(event.target) && !anchor?.contains(event.target))
        callbacks.current.onDismiss();
    };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!callbacks.current.busy) callbacks.current.onDismiss();
        return;
      }
      if (presentation === "dialog" && event.key === "Tab") {
        const items = focusable(element);
        const first = items[0], last = items[items.length - 1];
        if (!first) { event.preventDefault(); element.focus(); }
        else if (event.shiftKey && (aktivesDokument().activeElement === first || aktivesDokument().activeElement === element)) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (aktivesDokument().activeElement === last || !element.contains(aktivesDokument().activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    alleFenster.addEventListener("pointerdown", outside);
    alleFenster.addEventListener("keydown", keyboard, true);
    return () => {
      alleFenster.removeEventListener("pointerdown", outside);
      alleFenster.removeEventListener("keydown", keyboard, true);
      if ((aktivesDokument().activeElement === aktivesDokument().body || element.contains(aktivesDokument().activeElement)) && istDom(previous, "HTMLElement") && previous.isConnected)
        previous.focus();
    };
  }, [anchor, presentation, !!position]);

  const panel = (
    <div ref={surface} className={`kvp-rd office-open-surface office-open-${presentation}`}
      role="dialog" aria-modal={presentation === "dialog" ? true : undefined}
      aria-labelledby={headingId} aria-busy={busy} tabIndex={-1}
      style={presentation === "popover" ? { top: position?.top, left: position?.left, visibility: position ? "visible" : "hidden" } : undefined}>
      <div className="office-open-head">
        {presentation === "dialog" && <span className="office-open-heading-icon"><OfficeOpenIcon kind="document" /></span>}
        <h2 id={headingId}>{title}</h2>
        <button type="button" className="office-open-close" aria-label="Schließen" onClick={onDismiss} disabled={busy}>
          <OfficeOpenIcon kind="close" size={18} />
        </button>
      </div>
      {children}
    </div>
  );
  return createPortal(presentation === "dialog" ? (
    <div className="office-open-backdrop" onPointerDown={event => {
      if (event.target === event.currentTarget && !busy) onDismiss();
    }}>{panel}</div>
  ) : panel, document.body);
}
